<template>
  <content-modal
    :visible="visible"
    :close-aria-label="closeAriaLabel"
    :size="size"
    :auto-hide-off="autoHideOff"
    class="lesson-forum-topic-resource-delete-modal"
    @modal-shown="actionShown"
    @modal-hidden="actionHidden"
    @modal-hide-request="actionHideRequest"
  >
    <template #title>{{ t("teacher_reviews_delete_modal_confirmation_title") }}</template>
    <template #content>
      <modal-content>
        <template #content>
          {{ t("teacher_reviews_delete_modal_confirmation_subtitle") }}
        </template>
        <template #tools>
          <cv-button
            class="cancel"
            kind="tertiary"
            size="field"
            :disabled="isLoading"
            @click="actionHidden"
            >{{ t("button_cancel") }}
          </cv-button>
          <fetch-data-button
            :kind="deleteButton.kind"
            :size="deleteButton.size"
            :loading="isLoading"
            :disabled="isLoading || deleteButton.error"
            :error="deleteButton.error"
            :tip-position="deleteButton.tipPosition"
            @click="handleDeleteForumTopicResource"
            @reset-error-state="handleDeleteButtonResetError"
          >
            <span v-if="deleteButton.error">{{ t("button_state_error") }}</span>
            <span v-else>{{ t("button_ok") }}</span>
          </fetch-data-button>
        </template>
      </modal-content>
    </template>
  </content-modal>
</template>

<script setup lang="ts">
import modalEventBus from "@plugins/modal/service/event-bus";
import LessonReviewApi from "@apis/lesson.review.topic.api";
import { ref } from "vue";
import { FORUM_TOPIC_RESOURCE_TYPE } from "@modules/lesson-review/lesson.topic.constant.ts";
import { useI18n } from "vue-i18n";
import ModalContent from "@plugins/modal/components/ModalContent.vue";
import FetchDataButton from "@plugins/common/components/FetchDataButton.vue";
import ContentModal from "@plugins/modal/components/ContentModal.vue";
import { useVm } from "@utils/vm.ts";

const { vmNode } = useVm();
// Props typing
interface Params {
  topicType: string;
  topicResourceUuid: string;
  actionPrimary: () => void;
}
const props = defineProps<{ params: Params }>();

// i18n
const { t } = useI18n();

// State
const closeAriaLabel = "Close";
const size = "small";
const visible = ref(true);
const autoHideOff = true;
const isLoading = ref(false);

// Delete button state
const deleteButton = ref({
  kind: "primary",
  error: false,
  loading: false,
  errorMessage: t("api_action_generic_error_message"),
  size: undefined,
  tipPosition: undefined,
});

// Methods
function actionShown() {
  visible.value = true;
}

function actionHidden() {
  if (!isLoading.value) {
    visible.value = false;
    modalEventBus.$emit("modal-hidden", vmNode.key);
  }
}

function actionHideRequest(event: unknown) {
  if (event["cv:reason"] !== "external-click") {
    actionHidden();
  }
}

function resetProcessingState() {
  deleteButton.value.loading = false;
  deleteButton.value.error = false;
}

function setProcessingError() {
  deleteButton.value.loading = false;
  deleteButton.value.error = true;
  setTimeout(() => {
    resetProcessingState();
  }, 3000);
}

function handleDeleteButtonResetError() {
  deleteButton.value.error = false;
}

function handleDeleteForumTopicResource() {
  isLoading.value = true;
  let deleteAction;
  if (
    props.params.topicType === FORUM_TOPIC_RESOURCE_TYPE.LESSON_TEACHER_REVIEW ||
    props.params.topicType === FORUM_TOPIC_RESOURCE_TYPE.MOE_LIBRARY_TEACHER_REVIEW
  ) {
    deleteAction = LessonReviewApi.deleteLessonTeacherReviewTopicResource;
  } else {
    deleteAction = LessonReviewApi.deleteLessonAdminReviewTopicResource;
  }
  return new Promise<void>((resolve) => {
    deleteAction(props.params.topicResourceUuid).then((response) => {
      if (response.code !== "ok") {
        setProcessingError();
        isLoading.value = false;
      }
      handleSuccessDelete();
      resolve();
    });
  });
}

function handleSuccessDelete() {
  isLoading.value = false;
  visible.value = false;
  props.params.actionPrimary();
}
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/spacing";

.content-modal.lesson-forum-topic-resource-delete-modal {
  .bx--modal-container {
    &.bx--modal-container--sm {
      min-height: unset;
      .bx--modal-content {
        margin-bottom: $spacing-05;
      }
    }
  }
}
</style>
